import type { EventMemberRole } from "../collections/eventUsers";

export type EventInvitationRow = {
  email: string;
  role: EventMemberRole;
};

const VALID_ROLES = new Set<EventMemberRole>([
  "participant",
  "operator",
  "presenter",
  "manager",
]);

const detectDelimiter = (line: string): string => {
  if (line.includes("\t")) return "\t";
  if (line.includes(";")) return ";";
  return ",";
};

const parseCsvLine = (line: string, delimiter: string): string[] => {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (char === delimiter && !inQuotes) {
      cells.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }
  cells.push(current.trim());
  return cells;
};

const normalizeRole = (value: string | undefined): EventMemberRole => {
  const role = value?.trim().toLowerCase() as EventMemberRole | undefined;
  if (role && VALID_ROLES.has(role)) {
    return role;
  }
  return "participant";
};

const isHeaderRow = (line: string, delimiter: string): boolean => {
  const first = parseCsvLine(line, delimiter)[0]?.toLowerCase() ?? "";
  return first.includes("email") || first.includes("correo");
};

export function parseEventInvitationsCsv(text: string): EventInvitationRow[] {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return [];
  }

  const delimiter = detectDelimiter(lines[0]!);
  const startIndex = isHeaderRow(lines[0]!, delimiter) ? 1 : 0;
  const rows: EventInvitationRow[] = [];

  for (let i = startIndex; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]!, delimiter);
    const email = cells[0]?.trim();
    const role = normalizeRole(cells[1]);

    if (!email || !email.includes("@")) {
      continue;
    }

    rows.push({ email, role });
  }

  return rows;
}
